import React, {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "contexts";
import { SearchState, TestCategories, SortOrder } from "types";
import { buildFilters, handleQueryVariable } from "./SearchUtils";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

interface SearchProviderProps {
  children: ReactNode;
}

interface SearchContextType {
  searchState: SearchState;
  setSearchState: React.Dispatch<React.SetStateAction<SearchState>>;
  fetchSearchResults: () => Promise<Error | void>;
  initializeSearch: () => void;
  fetchNextResults: () => void;
  fetchPreviousResults: () => void;
}

const initialTestCategories = (): TestCategories => ({
  listening: {
    gte: null,
    lte: null,
  },
  overall: {
    gte: null,
    lte: null,
  },
  reading: {
    gte: null,
    lte: null,
  },
  speaking: {
    gte: null,
    lte: null,
  },
  writing: {
    gte: null,
    lte: null,
  },
});

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

export const SearchProvider = ({
  children,
}: SearchProviderProps): JSX.Element => {
  const { authState } = useAuth();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [searchState, setSearchState] = useState<SearchState>(() => {
    return {
      searchQuery: {
        query: "",
        limit: 5,
        sort: "",
        coordinates: {
          lon: null,
          lat: null,
          km: 10,
        },
        englishFilter: {
          ielts: initialTestCategories(),
          pte: initialTestCategories(),
          toefl: initialTestCategories(),
          cpe: initialTestCategories(),
          cae: initialTestCategories(),
        },
        testType: "",
        filters: {
          durationDay: {
            gte: null,
            lte: null,
          },
          estimatedTotalCourseFee: {
            gte: null,
            lte: null,
          },
          startDate: {
            gte: null,
            lte: null,
          },
          levels: [],
          universities: [],
          fieldsOfStudy: [],
          modesOfStudy: [],
          country: [],
        },
      },
      searchResults: {
        items: [],
        nextToken: "",
        total: 0,
        aggregateItems: [],
      },
      paginations: {
        currentPage: 1,
        totalResults: 0,
        startIndex: 0,
        endIndex: 0,
        nextTokens: "",
        previousTokens: [],
      },
      combinedFilters: undefined,
    };
  });

  const handleFetchSearchResults = useCallback(
    async (tokens: string | null = null) => {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      try {
        const baseVariables = handleQueryVariable(
          searchState.searchQuery.query,
          searchState.searchQuery.limit,
          searchState.searchQuery.filters,
          searchState.searchQuery.englishFilter,
          searchState.searchQuery.testType,
          searchState.searchQuery.coordinates,
          searchState.searchQuery.sort as SortOrder,
          tokens,
        );

        if (!searchState.searchResults.aggregateItems) {
          const unfiltered: any = await client.queries.searchCourses(
            { ...baseVariables, combinedFilters: undefined },
            {
              authMode: authState.isSignedIn ? "userPool" : "apiKey",
              ...(authState.isSignedIn && { authToken: token }),
            },
          );

          // console.log(unfiltered);

          // const unfiltered: any = await client.graphql({
          //   query: searchCoursesES,
          //   variables: { ...baseVariables, combinedFilters: undefined },
          //   authMode: authState.isSignedIn ? "userPool" : "iam",
          //   authToken: token,
          // });

          setSearchState((prev) => {
            try {
              const safeUnfilteredData = unfiltered?.data || {};
              const safeAggregateItems =
                safeUnfilteredData?.aggregateItems || [];

              return {
                ...prev,
                fullAggregations: safeAggregateItems,
              };
            } catch (error) {
              console.error(
                "Error in setSearchState (fullAggregations):",
                error,
                unfiltered,
              );
              return prev;
            }
          });
        }
        const result: any = await client.queries.searchCourses(baseVariables, {
          authMode: authState.isSignedIn ? "userPool" : "apiKey",
          ...(authState.isSignedIn && { authToken: token }),
        });

        setSearchState((prevState) => {
          try {
            const safeData = result?.data || {};
            const safeNextToken = safeData?.nextToken || "";
            const safeTotal = safeData?.total || 0;

            return {
              ...prevState,
              searchResults: { ...safeData },
              paginations: {
                ...prevState.paginations,
                nextTokens: safeNextToken,
                totalResults: safeTotal,
                startIndex:
                  (prevState.paginations.currentPage - 1) *
                    prevState.searchQuery.limit +
                  1,
                endIndex: Math.min(
                  prevState.paginations.currentPage *
                    prevState.searchQuery.limit,
                  safeTotal,
                ),
              },
            };
          } catch (error) {
            console.error("Error in setSearchState:", error, result);
            return prevState;
          }
        });
      } catch (error) {
        return error instanceof Error
          ? error
          : new Error(`Error fetching search results.`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      authState.isSignedIn,
      searchState.searchQuery.englishFilter,
      searchState.searchQuery.filters,
      searchState.searchQuery.coordinates,
      searchState.searchQuery.limit,
      searchState.searchQuery.query,
      searchState.searchQuery.sort,
    ],
  );

  const initializeSearch = useCallback(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      handleFetchSearchResults();
    }
  }, [hasInitialized, handleFetchSearchResults]);

  // Remove automatic useEffect that calls handleFetchSearchResults
  // useEffect(() => {
  //   handleFetchSearchResults();
  // }, [handleFetchSearchResults]);

  const generateCombinedFilters = useCallback(() => {
    const filterConditions = buildFilters(
      searchState.searchQuery.filters,
      true,
    );

    setSearchState((prevState) => {
      try {
        return {
          ...prevState,
          combinedFilters: filterConditions,
        };
      } catch (error) {
        console.error(
          "Error in setSearchState (combinedFilters):",
          error,
          filterConditions,
        );
        return prevState;
      }
    });
  }, [searchState.searchQuery.filters]);

  useEffect(() => {
    generateCombinedFilters();
  }, [generateCombinedFilters]);

  const fetchNextResults = () => {
    if (searchState.paginations.nextTokens) {
      handleFetchSearchResults(searchState.paginations.nextTokens);
      setSearchState((prevState) => {
        try {
          const safePreviousTokens = prevState.paginations.previousTokens || [];
          const safeNextTokens = prevState.paginations.nextTokens || "";
          const safeCurrentPage = prevState.paginations.currentPage || 1;

          return {
            ...prevState,
            paginations: {
              ...prevState.paginations,
              previousTokens: [...safePreviousTokens, safeNextTokens],
              nextTokens: "",
              currentPage: safeCurrentPage + 1,
            },
          };
        } catch (error) {
          console.error(
            "Error in setSearchState (fetchNextResults):",
            error,
            prevState,
          );
          return prevState;
        }
      });
    }
  };

  const fetchPreviousResults = () => {
    const safePreviousTokens = searchState.paginations.previousTokens || [];
    const prevToken = safePreviousTokens[safePreviousTokens.length - 2];

    handleFetchSearchResults(prevToken);
    setSearchState((prevState) => {
      try {
        const safePreviousTokens = prevState.paginations.previousTokens || [];
        const safeCurrentPage = prevState.paginations.currentPage || 1;

        return {
          ...prevState,
          paginations: {
            ...prevState.paginations,
            previousTokens: safePreviousTokens.slice(0, -1),
            nextTokens: prevToken || "",
            currentPage: Math.max(1, safeCurrentPage - 1),
          },
        };
      } catch (error) {
        console.error(
          "Error in setSearchState (fetchPreviousResults):",
          error,
          prevState,
        );
        return prevState;
      }
    });
  };

  return (
    <SearchContext.Provider
      value={{
        searchState,
        setSearchState,
        fetchSearchResults: handleFetchSearchResults,
        initializeSearch,
        fetchNextResults,
        fetchPreviousResults,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchProvider;
